import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import {
  ProtectedRoute,
  AdminRoute,
  SuperAdminRoute,
  CompanyAdminRoute,
} from "@/components/layout/route-guards";
import { UserLayout } from "@/components/layout/UserLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { ConsoleLayout } from "@/components/layout/ConsoleLayout";
import { Toaster } from "@/components/ui/Toaster";
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { RegisterPage } from "@/features/auth/pages/RegisterPage";
import { QuotePage } from "@/features/quote/pages/QuotePage";
import { HistoryPage } from "@/features/quote/pages/HistoryPage";
import { ImagePage } from "@/features/image/ImagePage";
import { CostPage } from "@/features/cost/pages/CostPage";
import { TemplatePage } from "@/features/template/pages/TemplatePage";
import { CompaniesPage } from "@/features/admin/companies/CompaniesPage";
import { UsersPage } from "@/features/admin/users/UsersPage";
import { ProductsPage } from "@/features/admin/products/ProductsPage";
import { ProductEditPage } from "@/features/admin/products/ProductEditPage";
import { PromptsPage } from "@/features/admin/prompts/PromptsPage";
import { LLMConfigPage } from "@/features/admin/llm-config/LLMConfigPage";
import { InvitesPage } from "@/features/admin/invites/InvitesPage";
import { InvitesRoute } from "@/features/admin/invites/InvitesRoute";
import { BillingPage } from "@/features/billing/pages/BillingPage";
import { AccountPage } from "@/features/account/AccountPage";

// 仪表盘带图表 (recharts), 懒加载成单独 chunk, 普通用户的报价页不用下载
const DashboardPage = lazy(() =>
  import("@/features/dashboard/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);

export function App() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        <Route
          element={
            <ProtectedRoute>
              <Outlet />
            </ProtectedRoute>
          }
        >
          {/* 前台: 顶栏 + tab (所有登录用户) */}
          <Route element={<UserLayout />}>
            <Route index element={<QuotePage />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="image" element={<ImagePage />} />
            <Route path="account" element={<AccountPage />} />
            <Route path="template" element={<TemplatePage />} />
          </Route>

          {/* 所有登录用户可见：普通用户只看到自己的数据 (后端按角色隔离)，标题为「我的用量」。
              普通用户套前台布局, 管理员套后台布局 */}
          <Route element={<ConsoleLayout />}>
            <Route path="cost" element={<CostPage />} />
          </Route>

          {/* 管理后台: 侧边栏布局 (管理员) */}
          <Route
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            {/* 管理员首页 (homePathFor) */}
            <Route
              path="dashboard"
              element={
                <AdminRoute>
                  <Suspense fallback={<div className="p-6 text-sm text-muted-foreground">加载中…</div>}>
                    <DashboardPage />
                  </Suspense>
                </AdminRoute>
              }
            />
            <Route
              path="billing"
              element={
                <CompanyAdminRoute>
                  <BillingPage />
                </CompanyAdminRoute>
              }
            />

            {/* 产品：一级目录，列表与编辑分开 */}
            <Route
              path="products"
              element={
                <AdminRoute>
                  <Outlet />
                </AdminRoute>
              }
            >
              <Route index element={<ProductsPage />} />
              <Route path="new" element={<ProductEditPage />} />
              <Route path=":id" element={<ProductEditPage />} />
            </Route>

            {/* 原 /admin 页内子 tab 已并入后台侧边栏 */}
            <Route
              path="admin"
              element={
                <AdminRoute>
                  <Outlet />
                </AdminRoute>
              }
            >
              <Route index element={<Navigate to="users" replace />} />
              <Route
                path="companies"
                element={
                  <SuperAdminRoute>
                    <CompaniesPage />
                  </SuperAdminRoute>
                }
              />
              <Route path="users" element={<UsersPage />} />
              <Route
                path="invites"
                element={
                  <InvitesRoute>
                    <InvitesPage />
                  </InvitesRoute>
                }
              />
              <Route
                path="prompts"
                element={
                  <SuperAdminRoute>
                    <PromptsPage />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="llm-config"
                element={
                  <SuperAdminRoute>
                    <LLMConfigPage />
                  </SuperAdminRoute>
                }
              />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </>
  );
}
